import { Helmet } from "react-helmet-async";

/* ═══════════════════════════════════════════════════════════════
   SEO Component — Reusable meta tags for every page
   Includes: Title, Description, Open Graph, Twitter Cards,
   Canonical URL, Schema markup
   ═══════════════════════════════════════════════════════════════ */

const SITE_URL = "https://techtcl.com";
const SITE_NAME = "TechTCL";
const DEFAULT_IMAGE = "https://techtcl.com/logo.png";

export default function SEO({
  title,
  description,
  path = "",
  image = DEFAULT_IMAGE,
  type = "website",
  schema = null,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Shopify Growth & Development Partner`;
  const canonicalUrl = `${SITE_URL}${path}`;

  return (
    <Helmet>
      {/* Basic */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {/* Schema */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
}

/* ═══ Pre-built Schema Templates ═══ */

export const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "name": "TechTCL",
  "alternateName": "Tech Talent for Creative Leadership",
  "url": SITE_URL,
  "logo": DEFAULT_IMAGE,
  "description": "Certified Shopify Growth Partner delivering premium e-commerce development, custom software, mobile apps, and data analytics solutions across 15+ countries.",
  "telephone": "+8801837127779",
  "email": "support@techtcl.com",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Dhaka",
    "addressCountry": "BD"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "23.8103",
    "longitude": "90.4125"
  },
  "priceRange": "$3,000 - $25,000+",
  "openingHoursSpecification": {
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    "opens": "09:00",
    "closes": "18:00"
  },
  "sameAs": [
    "https://www.linkedin.com/in/noyonuzzaman-techtcl/",
    "https://www.facebook.com/profile.php?id=61578997427316"
  ],
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "5.0",
    "reviewCount": "150",
    "bestRating": "5",
    "worstRating": "1"
  }
};

export const serviceSchema = (name, description, url) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "name": name,
  "description": description,
  "url": `${SITE_URL}${url}`,
  "provider": {
    "@type": "ProfessionalService",
    "name": "TechTCL",
    "url": SITE_URL
  },
  "areaServed": "Worldwide",
  "serviceType": name,
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(f => ({
    "@type": "Question",
    "name": f.q,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": f.a
    }
  }))
});

export const reviewSchema = (reviews) => ({
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "name": "TechTCL",
  "url": SITE_URL,
  "review": reviews.map(r => ({
    "@type": "Review",
    "author": { "@type": "Person", "name": r.name },
    "reviewBody": r.text,
    "reviewRating": {
      "@type": "Rating",
      "ratingValue": "5",
      "bestRating": "5"
    }
  }))
});
